import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PromoCode, PromoCodeUsage } from '@/types/admin';
import { useAuditLog } from './useAuditLog';
import { toast } from 'sonner';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

// Type alias for cleaner code
type PromoCodeRow = Tables<'promo_codes'>;
type PromoCodeInsert = TablesInsert<'promo_codes'>;
type PromoCodeUpdate = TablesUpdate<'promo_codes'>;

export function usePromoCodes() {
  const [loading, setLoading] = useState(false);
  const { logAction } = useAuditLog();

  const getPromoCodes = useCallback(async (): Promise<PromoCode[]> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as PromoCode[];
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      toast.error('無法獲取折扣碼: ' + message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getPromoCode = useCallback(async (id: string): Promise<PromoCode | null> => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as PromoCode;
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      toast.error('無法獲取折扣碼詳情: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createPromoCode = useCallback(async (promoData: Partial<PromoCode>): Promise<PromoCode | null> => {
    setLoading(true);
    try {
      const insertData: PromoCodeInsert = {
        code: promoData.code ?? '',
        discount_type: promoData.discount_type ?? 'percentage',
        description: promoData.description,
        discount_value: promoData.discount_value,
        currency: promoData.currency ?? 'TWD',
        applicable_plans: promoData.applicable_plans ?? [],
        max_uses: promoData.max_uses,
        starts_at: promoData.starts_at,
        expires_at: promoData.expires_at,
        is_active: promoData.is_active ?? true,
      };

      const { data, error } = await supabase
        .from('promo_codes')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Sync to Stripe
      try {
        // 處理不同的折扣類型
        let percent_off: number | undefined;
        let amount_off: number | undefined;
        let duration: string = 'forever';
        let duration_in_months: number | undefined;
        
        if (promoData.discount_type === 'percentage') {
          percent_off = promoData.discount_value ?? undefined;
        } else if (promoData.discount_type === 'fixed') {
          amount_off = ((promoData.discount_value || 0) * 100); // 轉換為分
        } else if (promoData.discount_type === 'free_month') {
          percent_off = 100; // 100% 折扣
          duration = 'repeating';
          duration_in_months = 1; // 只適用一個月
        }
        
        await supabase.functions.invoke('stripe-sync', {
            body: { 
                action: 'sync_promo', 
                payload: {
                    code: promoData.code,
                    percent_off,
                    amount_off,
                    currency: 'usd',
                    duration,
                    duration_in_months,
                    max_redemptions: promoData.max_uses || undefined,
                    expires_at: promoData.expires_at || undefined,
                }
            }
        });
      } catch (stripeError) {
        console.error('Stripe sync failed:', stripeError);
        toast.error('Stripe 同步失敗，但本地已建立');
      }

      await logAction({
        action: 'promo.create',
        targetType: 'promo_code',
        targetId: data.id,
        details: { code: data.code },
      });

      toast.success('折扣碼已建立');
      return data as PromoCode;
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      toast.error('建立折扣碼失敗: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [logAction]);

  const updatePromoCode = useCallback(async (id: string, updates: Partial<PromoCode>): Promise<PromoCode | null> => {
    setLoading(true);
    try {
      const updateData: PromoCodeUpdate = {
        code: updates.code,
        description: updates.description,
        discount_type: updates.discount_type,
        discount_value: updates.discount_value,
        currency: updates.currency,
        applicable_plans: updates.applicable_plans,
        max_uses: updates.max_uses,
        starts_at: updates.starts_at,
        expires_at: updates.expires_at,
        is_active: updates.is_active,
      };

      const { data, error } = await supabase
        .from('promo_codes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      if (updates.is_active === false) {
          try {
            await supabase.functions.invoke('stripe-sync', {
                body: { 
                    action: 'delete_promo', 
                    payload: { code: data.code } 
                }
            });
          } catch (stripeError) {
             console.error('Stripe sync failed:', stripeError);
          }
      }

      await logAction({
        action: 'promo.update',
        targetType: 'promo_code',
        targetId: id,
        details: updates,
      });

      toast.success('折扣碼已更新');
      return data as PromoCode;
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      toast.error('更新折扣碼失敗: ' + message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [logAction]);

  const getPromoCodeUsages = useCallback(async (promoCodeId: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_code_usages')
        .select('*, profiles(email)')
        .eq('promo_code_id', promoCodeId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      toast.error('無法獲取使用記錄: ' + message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    getPromoCodes,
    getPromoCode,
    createPromoCode,
    updatePromoCode,
    getPromoCodeUsages,
  };
}
